import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import {
  Heart,
  Globe,
  ShieldCheck,
  Building2,
  Users,
  Award,
  MapPin,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_info/about")({
  head: () => ({
    meta: [
      { title: "About Us — OyoBooking" },
      {
        name: "description",
        content:
          "OyoBooking is the premium reservation platform for hotels, event centres and open spaces across Oyo State, Nigeria.",
      },
    ],
    links: [{ rel: "canonical", href: "https://oyobooking.ng/about" }],
  }),
  component: AboutPage,
});

function AnimatedCounter({
  target,
  suffix = "",
  decimals = 0,
  duration = 2500,
}: {
  target: number;
  suffix?: string;
  decimals?: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const [inView, setInView] = useState(false);
  const elementRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;

    let startTimestamp: number | null = null;
    let animationFrameId: number;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // Easing out quad formula: progress * (2 - progress)
      const easeProgress = progress * (2 - progress);
      setCount(easeProgress * target);
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };

    animationFrameId = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [inView, target, duration]);

  return (
    <span ref={elementRef}>
      {count.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}

const VALUES = [
  {
    icon: Heart,
    title: "Local First",
    body: "Every single venue and hotel is hand-picked and verified from communities we love and live in.",
    color: "from-rose-500/20 to-orange-500/20",
    iconBg: "bg-gradient-to-br from-rose-500 to-orange-500",
  },
  {
    icon: Globe,
    title: "Statewide Reach",
    body: "From the bustling streets of Ibadan to Saki, Ogbomoso and Oyo town — we cover the entire state.",
    color: "from-blue-500/20 to-cyan-500/20",
    iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500",
  },
  {
    icon: ShieldCheck,
    title: "Trust & Transparency",
    body: "Verified hosts, direct communications, secure payments and authentic reviews from real guests.",
    color: "from-emerald-500/20 to-teal-500/20",
    iconBg: "bg-gradient-to-br from-emerald-500 to-teal-500",
  },
];

const STATS = [
  { target: 1200, suffix: "+", label: "Verified Venues", subLabel: "Properties listed", icon: Building2 },
  { target: 48, suffix: "K+", label: "Happy Guests", subLabel: "Bookings completed", icon: Users },
  { target: 4.8, suffix: "★", decimals: 1, label: "Average Rating", subLabel: "From customer reviews", icon: Award },
  { target: 33, suffix: "+", label: "Local LGAs", subLabel: "Covered across Oyo State", icon: MapPin },
];

function AboutPage() {
  return (
    <>
      {/* ── Hero Section ──────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-gold/5 pb-8 pt-16 sm:pb-12 sm:pt-20">
        {/* Decorative floating orbs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -left-20 top-10 size-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -right-20 bottom-10 size-80 rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute left-1/2 top-1/3 size-48 -translate-x-1/2 rounded-full bg-primary/3 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="animate-fade-up">
            <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Our Story
            </span>
          </div>

          <h1 className="animate-fade-up delay-75 mt-6 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            Built in Ibadan, for all of{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Oyo State.
              </span>
              <span className="absolute -bottom-1 left-0 h-3 w-full rounded bg-gold/20" aria-hidden="true" />
            </span>
          </h1>

          <p className="animate-fade-up delay-150 mx-auto mt-6 text-lg leading-relaxed text-muted-foreground max-w-2xl">
            OyoBooking was born from a simple idea — finding and reserving a great place in Oyo State should be as easy as sending a text. We partner with local hosts to bring you the most thoughtfully curated stays and venues across the state.
          </p>
        </div>
      </section>

      {/* ── Values / What drives us ───────────────────────────── */}
      <section className="relative -mt-1 bg-gradient-warm py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-up delay-75">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">What drives us</h2>
            <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
              We guide every decision with local community care, high quality standards, and technical reliability.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => {
              const delayClass = ["delay-150", "delay-225", "delay-300"][i] || "";
              return (
                <div
                  key={v.title}
                  className={`animate-fade-up ${delayClass} group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-elegant`}
                >
                  {/* Hover gradient glow */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${v.color} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                  />
                  <div className="relative">
                    <div
                      className={`${v.iconBg} grid size-12 place-items-center rounded-xl text-white shadow-lg transition-transform duration-300 group-hover:scale-110`}
                    >
                      <v.icon className="size-6" />
                    </div>
                    <h3 className="mt-6 font-display text-xl font-semibold">{v.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Stats Display ─────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => {
            const delayClass = ["delay-150", "delay-225", "delay-300", "delay-375"][i] || "";
            return (
              <div
                key={s.label}
                className={`animate-fade-up ${delayClass} group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-8 text-center transition-all duration-300 hover:border-primary/30`}
              >
                <div className="mx-auto mb-4 grid size-12 place-items-center rounded-full bg-accent text-primary transition-transform duration-300 group-hover:scale-110">
                  <s.icon className="size-5" />
                </div>
                <div className="font-display text-4xl font-bold text-primary">
                  <AnimatedCounter
                    target={s.target}
                    suffix={s.suffix}
                    decimals={s.decimals}
                    duration={2500}
                  />
                </div>
                <div className="mt-2 text-sm font-semibold">{s.label}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{s.subLabel}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Empowering Host Economy Bottom CTA ────────────────── */}
      <section className="border-t border-border/60 bg-gradient-warm">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              <TrendingUp className="size-3.5" />
              Empowering Host Economy
            </span>
          </div>
          <h2 className="animate-fade-up delay-75 mt-6 font-display text-3xl font-bold sm:text-4xl">
            Put your space to work.
          </h2>
          <p className="animate-fade-up delay-150 mx-auto mt-4 max-w-xl text-muted-foreground">
            Whether you own a luxury hotel, a boutique event space, or a scenic garden, OyoBooking helps you reach guests looking for their next perfect experience.
          </p>
          <div className="animate-fade-up delay-225 mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/list-your-property">
              <Button size="lg" className="gap-2">
                List your property
                <ArrowRight className="size-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline">
                Talk to support
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
